import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  House, Users, Clock, ShieldCheck, AirplaneTilt, Coins, Gift, HandCoins,
  Receipt, ChartBar, ListMagnifyingGlass, SignOut, CaretRight, Calculator, Buildings, ChartPie,
  MapPin, MapTrifold, Key, IdentificationBadge,
} from "@phosphor-icons/react";

const NAV_BASE = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/attendance", label: "Attendance", icon: Clock, testid: "nav-attendance" },
  { to: "/attendance-review", label: "Verification", icon: ShieldCheck, testid: "nav-verification" },
  { to: "/leave", label: "Leave", icon: AirplaneTilt, testid: "nav-leave" },
  { to: "/fines", label: "Fines", icon: Coins, testid: "nav-fines" },
  { to: "/incentives", label: "Incentives", icon: Gift, testid: "nav-incentives" },
  { to: "/advances", label: "Advances & Loans", icon: HandCoins, testid: "nav-advances" },
  { to: "/payroll", label: "Auto Payroll", icon: Receipt, testid: "nav-payroll" },
  { to: "/manual-payroll", label: "Manual Payroll", icon: Calculator, testid: "nav-manual-payroll" },
  { to: "/payroll-reports", label: "Payroll Reports", icon: ChartPie, testid: "nav-payroll-reports" },
  { to: "/locations", label: "Locations", icon: MapPin, testid: "nav-locations" },
  { to: "/location-compliance", label: "Geo-Fence Report", icon: MapTrifold, testid: "nav-location-compliance" },
  { to: "/reports", label: "Operations", icon: ChartBar, testid: "nav-reports" },
  { to: "/company-settings", label: "Company", icon: Buildings, testid: "nav-company" },
  { to: "/verify", label: "EthicCred Verify", icon: IdentificationBadge, testid: "nav-verify" },
];
const NAV_ADMIN_ONLY = [
  { to: "/hr-access", label: "HR Access", icon: Key, testid: "nav-hr-access", roles: ["super_admin"] },
  { to: "/audit", label: "Audit Log", icon: ListMagnifyingGlass, testid: "nav-audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  async function onLogout() {
    const dest = user?.role === "super_admin" ? "/adentry" : "/hr";
    await logout();
    nav(dest, { replace: true });
  }

  const NAV = [...NAV_BASE,
    ...NAV_ADMIN_ONLY.filter((n) => !n.roles || n.roles.includes(user?.role))];

  return (
    <div className="min-h-screen flex bg-[#0D0D10] text-white">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-[#2D2D35] bg-[#0D0D10]">
        <div className="px-5 py-5 border-b border-[#2D2D35]">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5" data-testid="admin-sidebar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors duration-200
                 ${isActive
                    ? "bg-[#E50914]/10 text-white border-l-2 border-[#E50914] pl-[10px]"
                    : "text-zinc-400 hover:text-white hover:bg-[#16161C] border-l-2 border-transparent"}`
              }
            >
              <n.icon size={18} weight="bold" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#2D2D35]">
          <div className="px-3 py-2 text-xs text-zinc-500">
            <div className="text-white text-sm font-medium" data-testid="topbar-user-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#E50914] mt-0.5">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:text-white hover:bg-[#16161C] rounded-md transition-colors duration-200"
          >
            <SignOut size={16} weight="bold" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-[#2D2D35] bg-[#0D0D10] sticky top-0 z-30">
          <Logo size="sm" />
          <button onClick={onLogout} className="text-xs text-zinc-400" data-testid="logout-button-mobile">Sign out</button>
        </header>

        {/* Mobile horizontal nav */}
        <div className="md:hidden overflow-x-auto border-b border-[#2D2D35] bg-[#0D0D10]">
          <div className="flex gap-1 px-2 py-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 text-xs rounded-md ${isActive ? "bg-[#E50914] text-white" : "bg-[#16161C] text-zinc-300"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
